// src/App.tsx - SIMPLIFIED: Direct React Router Navigation
import React, { useState, useEffect } from 'react';
import { useAuth } from './hooks/useAuth';
import { SelectedLocation } from './types';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Existing page components
import LandingPage from './components/landing/LandingPage';
import PreviewPage from './components/preview/PreviewPage';
import QuickSignupPage from './components/auth/QuickSignupPage';
import LoginPage from './components/auth/LoginPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import OnboardingPage from './components/onboarding/OnboardingPage';
import DashboardPage from './components/dashboard/DashboardPage';
import MessagesPage from './components/messaging/MessagesPage';
import DiscoverPage from './components/discover/DiscoverPage';
import ProfilePage from './components/profile/ProfilePage';
import ProfileViewPage from './components/profile/ProfileViewPage';
import ProfileEditPage from './components/profile/ProfileEditPage';
import ProfileImageUploadPage from './components/profile/ProfileImageUploadPage';
import CondosPage from './components/condos/CondosPage';
import CondoViewPage from './components/condos/CondoViewPage';
import CondoShortRedirect from './components/condos/CondoShortRedirect';

// Common components
import ProtectedRoute from './components/common/ProtectedRoute';
import AdminProtectedRoute from './components/common/AdminProtectedRoute';
import PageTitle from './components/common/PageTitle';

// Admin components
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used in Route path /admin/dashboard
import AdminDashboardPage from './components/admin/AdminDashboardPage';
import AdminCondoFillPage from './components/admin/AdminCondoFillPage';
import AdminCondosListPage from './components/admin/AdminCondosListPage';
import AdminPendingCondosPage from './components/admin/AdminPendingCondosPage';
import AdminStatusPage from './components/admin/AdminStatusPage';
import AdminBroadcastPage from './components/admin/AdminBroadcastPage';
import AdminHealthPage from './components/admin/AdminHealthPage';
import BroadcastFeedbackPage from './components/common/BroadcastFeedbackPage';

// User condo submission components
import MyCondosPage from './components/condos/MyCondosPage';

// Public informational pages
import HowItWorksPage from './components/how-it-works/HowItWorksPage';

// Utilities
import { getPageUrl } from './utils/urlHelpers';

// Styles
import './styles/globals.css';
import './styles/App.css';

type PageType = 'landing' | 'preview' | 'signup' | 'login' | 'onboarding' | 'dashboard' | 'matches' | 'messages' | 'discover';

// Keep existing AppContent for backward compatibility
const AppContent: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<PageType>('landing');
  const [, setSelectedLocations] = useState<SelectedLocation[]>([]);
  const { user, loading } = useAuth();

  // Dynamic page titles
  useEffect(() => {
    const pageTitles: Record<PageType, string> = {
      landing: 'ShiYou - Find Your Perfect Roommate',
      preview: 'ShiYou - Preview Profiles',
      signup: 'ShiYou - Create Account',
      login: 'ShiYou - Sign In',
      onboarding: 'ShiYou - Complete Your Profile',
      dashboard: 'ShiYou - Dashboard',
      matches: 'ShiYou - Your Matches',
      messages: 'ShiYou - Messages',
      discover: 'ShiYou - Discover Roommates'
    };

    const baseTitle = 'ShiYou';
    const pageTitle = pageTitles[currentPage] || baseTitle;
    document.title = pageTitle;
  }, [currentPage]);

  const navigate = (page: string, locations?: SelectedLocation[]) => {
    if (locations) {
      setSelectedLocations(locations);
    }
    setCurrentPage(page as PageType);
  };

  // Simplified redirect logic - only redirect when user completes onboarding
  useEffect(() => {
    if (!loading && user) {
      // Only redirect from onboarding to dashboard when onboarding is completed
      if (user.onboardingCompleted && currentPage === 'onboarding') {
        setCurrentPage('dashboard');
      }
      // Only redirect from login/signup to onboarding when user needs onboarding
      else if (!user.onboardingCompleted && (currentPage === 'login' || currentPage === 'signup')) {
        setCurrentPage('onboarding');
      }
      // Only redirect from login/signup to dashboard when user is fully set up
      else if (user.onboardingCompleted && (currentPage === 'login' || currentPage === 'signup')) {
        setCurrentPage('dashboard');
      }
    }
  }, [user, loading, currentPage]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="text-coffee/40 text-sm">Loading...</div>
      </div>
    );
  }

  // Route to appropriate page
  switch (currentPage) {
    case 'landing':
      return <LandingPage onNavigate={navigate} />;
      
    case 'preview':
      return <PreviewPage onNavigate={navigate} />;
      
    case 'signup':
      return <QuickSignupPage onNavigate={navigate} />;
      
    case 'login':
      return <LoginPage onNavigate={navigate} />;
      
    case 'onboarding':
      return <OnboardingPage onNavigate={navigate} />;
      
    case 'dashboard':
      return <DashboardPage onNavigate={navigate} />;
      
    case 'matches':
      return <DiscoverPage onNavigate={navigate} />;
      
    case 'messages':
      return <MessagesPage onNavigate={navigate} />;
      
    case 'discover':
      return <DiscoverPage onNavigate={navigate} />;
      
    default:
      return <LandingPage onNavigate={navigate} />;
  }
};

// New React Router wrapper
const App: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Redirect /landing to / */}
        <Route path="/landing" element={<Navigate to="/" replace />} />
        
        {/* Public Routes */}
        <Route path="/" element={
          <PageTitle title="Find Your Perfect Roommate">
            <LandingPage onNavigate={(page) => window.location.href = getPageUrl(`/${page}`)} />
          </PageTitle>
        } />
        <Route path="/preview" element={
          <PageTitle title="Preview Profiles">
            <PreviewPage onNavigate={(page) => window.location.href = getPageUrl(`/${page}`)} />
          </PageTitle>
        } />
        <Route path="/signup" element={
          <PageTitle title="Create Account">
            <QuickSignupPage onNavigate={(page) => window.location.href = getPageUrl(`/${page}`)} />
          </PageTitle>
        } />
        <Route path="/login" element={
          <PageTitle title="Sign In">
            <LoginPage onNavigate={(page) => window.location.href = getPageUrl(`/${page}`)} />
          </PageTitle>
        } />
        <Route path="/forgot-password" element={
          <PageTitle title="Forgot Password">
            <ForgotPasswordPage />
          </PageTitle>
        } />
        <Route path="/reset-password" element={
          <PageTitle title="Reset Password">
            <ResetPasswordPage />
          </PageTitle>
        } />
        <Route path="/how-it-works" element={
          <PageTitle title="How It Works">
            <HowItWorksPage />
          </PageTitle>
        } />
        <Route path="/c/:shortId" element={
          <PageTitle title="Condo Details">
            <CondoShortRedirect />
          </PageTitle>
        } />

        {/* Protected Routes */}
        <Route 
          path="/onboarding" 
          element={
            <PageTitle title="Complete Your Profile">
              <ProtectedRoute requireOnboarding={false}>
                <OnboardingPage onNavigate={(page) => window.location.href = getPageUrl(`/${page}`)} />
              </ProtectedRoute>
            </PageTitle>
          } 
        />
        
        <Route 
          path="/dashboard" 
          element={
            <PageTitle title="Dashboard">
              <ProtectedRoute>
                <DashboardPage onNavigate={(page) => window.location.href = getPageUrl(`/${page}`)} />
              </ProtectedRoute>
            </PageTitle>
          } 
        />
        
        
        <Route 
          path="/messages" 
          element={
            <PageTitle title="Messages">
              <ProtectedRoute>
                <MessagesPage onNavigate={(page) => window.location.href = getPageUrl(`/${page}`)} />
              </ProtectedRoute>
            </PageTitle>
          } 
        />
        
        <Route 
          path="/messages/:conversationId" 
          element={
            <PageTitle title="Messages">
              <ProtectedRoute>
                <MessagesPage onNavigate={(page) => window.location.href = getPageUrl(`/${page}`)} />
              </ProtectedRoute>
            </PageTitle>
          } 
        />
        
        <Route
          path="/discover"
          element={
            <PageTitle title="Discover Roommates">
              <DiscoverPage onNavigate={(page) => window.location.href = getPageUrl(`/${page}`)} />
            </PageTitle>
          }
        />
        
        <Route
          path="/condos"
          element={
            <PageTitle title="Discover Condos">
              <CondosPage />
            </PageTitle>
          }
        />
        <Route
          path="/condos/edit/:condoId"
          element={
            <PageTitle title="Edit Your Condo">
              <ProtectedRoute>
                <AdminCondoFillPage mode="user" />
              </ProtectedRoute>
            </PageTitle>
          }
        />
        <Route
          path="/condos/:condoId"
          element={
            <PageTitle title="Condo Details">
              <CondoViewPage />
            </PageTitle>
          }
        />
        <Route
          path="/condos/create"
          element={
            <PageTitle title="List Your Condo">
              <ProtectedRoute>
                <AdminCondoFillPage mode="user" />
              </ProtectedRoute>
            </PageTitle>
          }
        />
        <Route
          path="/my-condos"
          element={
            <PageTitle title="My Condos">
              <ProtectedRoute>
                <MyCondosPage />
              </ProtectedRoute>
            </PageTitle>
          }
        />
        <Route 
          path="/profile" 
          element={
            <PageTitle title="My Profile">
              <ProtectedRoute>
                <ProfilePage onNavigate={(page) => window.location.href = getPageUrl(`/${page}`)} />
              </ProtectedRoute>
            </PageTitle>
          } 
        />
        
        <Route 
          path="/profile/view/:userId" 
          element={
            <PageTitle title="View Profile">
              <ProtectedRoute>
                <ProfileViewPage />
              </ProtectedRoute>
            </PageTitle>
          } 
        />
        
        <Route 
          path="/profile/edit" 
          element={
            <PageTitle title="Edit Profile">
              <ProtectedRoute>
                <ProfileEditPage onNavigate={(page) => window.location.href = getPageUrl(`/${page}`)} />
              </ProtectedRoute>
            </PageTitle>
          } 
        />
        
        <Route 
          path="/profile/image" 
          element={
            <PageTitle title="Upload Profile Photo">
              <ProtectedRoute>
                <ProfileImageUploadPage onNavigate={(page) => window.location.href = getPageUrl(`/${page}`)} />
              </ProtectedRoute>
            </PageTitle>
          } 
        />
        
        {/* Admin Routes */}
        <Route 
          path="/admin/dashboard" 
          element={
            <PageTitle title="Admin Dashboard">
              <AdminProtectedRoute>
                <AdminDashboardPage />
              </AdminProtectedRoute>
            </PageTitle>
          } 
        />
        <Route 
          path="/admin/condos" 
          element={
            <PageTitle title="Admin - Condo Management">
              <AdminProtectedRoute>
                <AdminCondoFillPage />
              </AdminProtectedRoute>
            </PageTitle>
          } 
        />
        <Route
          path="/admin/condos/list"
          element={
            <PageTitle title="Admin - View Condos">
              <AdminProtectedRoute>
                <AdminCondosListPage />
              </AdminProtectedRoute>
            </PageTitle>
          }
        />
        <Route
          path="/admin/condos/drafts"
          element={
            <PageTitle title="Admin - Incomplete Condos">
              <AdminProtectedRoute>
                <AdminCondosListPage isDrafts={true} />
              </AdminProtectedRoute>
            </PageTitle>
          }
        />
        <Route
          path="/admin/condos/pending"
          element={
            <PageTitle title="Admin - Review Submissions">
              <AdminProtectedRoute>
                <AdminPendingCondosPage />
              </AdminProtectedRoute>
            </PageTitle>
          }
        />
        
        <Route
          path="/admin/condos/edit/:condoId"
          element={
            <PageTitle title="Admin - Edit Condo">
              <AdminProtectedRoute>
                <AdminCondoFillPage />
              </AdminProtectedRoute>
            </PageTitle>
          }
        />
        <Route
          path="/admin/status"
          element={
            <PageTitle title="Admin - Status Board">
              <AdminProtectedRoute>
                <AdminStatusPage />
              </AdminProtectedRoute>
            </PageTitle>
          }
        />
        <Route
          path="/admin/condos/preview/:condoId"
          element={
            <PageTitle title="Admin - Condo Preview">
              <AdminProtectedRoute>
                <CondoViewPage />
              </AdminProtectedRoute>
            </PageTitle>
          }
        />

        <Route
          path="/admin/broadcasts"
          element={
            <PageTitle title="Admin - Broadcasts">
              <AdminProtectedRoute>
                <AdminBroadcastPage />
              </AdminProtectedRoute>
            </PageTitle>
          }
        />

        <Route
          path="/admin/health"
          element={
            <PageTitle title="Admin - User Health">
              <AdminProtectedRoute>
                <AdminHealthPage />
              </AdminProtectedRoute>
            </PageTitle>
          }
        />

        {/* Public feedback page — no auth guard */}
        <Route
          path="/broadcast-feedback/:broadcastId"
          element={
            <PageTitle title="Feedback">
              <BroadcastFeedbackPage />
            </PageTitle>
          }
        />

        {/* Fallback to old system for any unmatched routes */}
        <Route path="*" element={<AppContent />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;